import { INotificationMessage } from "@fl/shared/src/types/messages";
import { ConfiguracoesImpostosAttributes } from "@fl/shared/src/types/models/suporte_fl/impostos";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { InferType } from "yup";
import AlterMessages from "../../../components/alerts/indes";
import SelectComp from "../../../components/select/selectComp";
import { configImpostosSchema } from "../../../schemas/configuracoesImpostos";
import ApiConfigImpostos from "../../../services/suporte/auth/configs/impostos";
import ApiFiscal from "../../../services/suporte/auth/fiscal";
import DefaultBasePage from "../../default/base";

export type ConfigImpostosFormInputs = InferType<typeof configImpostosSchema>;

/**
 * Helpers de conversão:
 * - toStr: number|null|undef -> string|null
 * - toNum: string|null|undef -> number|null
 */
const toStr = (n?: number | null) => (n == null ? null : String(n));
const toNum = (s?: string | null) => (s == null || s === "" ? null : Number(s));

/**
 * Normaliza dados vindos da API (números) para o FORM (strings)
 */
const normalizeToForm = (
  raw: Partial<ConfiguracoesImpostosAttributes> = {}
) => ({
  id_configuracao: toStr(raw.id_configuracao),
  id_pis: toStr(raw.id_pis),
  id_inss: toStr(raw.id_inss),
  id_iss: toStr(raw.id_iss),
  id_ir: toStr(raw.id_ir),
  id_fgts: toStr(raw.id_fgts),
  id_fgts_13: toStr(raw.id_fgts_13),
  id_inss_13: toStr(raw.id_inss_13),
});

/**
 * Converte dados do FORM (strings) para payload da API (números)
 */
const formToPayload = (data: ConfigImpostosFormInputs) => ({
  id_configuracao: toNum((data as any).id_configuracao),
  id_pis: toNum(data.id_pis as any),
  id_inss: toNum(data.id_inss as any),
  id_iss: toNum(data.id_iss as any),
  id_ir: toNum(data.id_ir as any),
  id_fgts: toNum(data.id_fgts as any),
  id_fgts_13: toNum(data.id_fgts_13 as any),
  id_inss_13: toNum(data.id_inss_13 as any),
});

const ConfigImpostosPrivatePage = () => {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [options, setOptions] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfigImpostosFormInputs>({
    resolver: yupResolver(configImpostosSchema),
    defaultValues: normalizeToForm(), // mantém coerência com strings no form
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        // 1) Carrega lista de impostos e monta options (value/label como string)
        const listaImpostos = await new ApiFiscal().listImpostos();
        if (listaImpostos.success) {
          const arrayData = Object.values(listaImpostos.data);

          // substitua o trecho que calcula formattedOptions por:
          const formattedOptions = arrayData
            .map((imp: any) => {
              // tenta chaves comuns para o ID
              const rawId =
                imp.id_imposto ??
                imp.id ??
                imp.id_rv2_cod_imp ?? // se for esse, ok
                imp.codigo ??
                imp.cod ??
                null;

              if (rawId == null) return null; // descarta itens sem id

              return {
                value: String(rawId), // SEMPRE string no select
                label: `${imp.st_conta_cont} - ${imp.st_rv2_nome_imp}`,
              };
            })
            .filter(Boolean) as { value: string; label: string }[];

          // getSelectOptions já retorna {value: string; label: string}
          setOptions(formattedOptions);
        } else if (listaImpostos.message) {
          setMessages(listaImpostos.message);
        }

        // 2) Carrega configuração atual e aplica no form (strings)
        const result = await new ApiConfigImpostos().list();
        if (result.success) {
          const apiItem: ConfiguracoesImpostosAttributes =
            (result.data && result.data[0]) || {};
          reset(normalizeToForm(apiItem));
        } else if (result.message) {
          setMessages(result.message);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [reset]);

  const onSubmit = async (data: ConfigImpostosFormInputs) => {
    setLoading(true);
    try {
      // Converte strings do form em números para enviar
      const payload = formToPayload(data);

      const result = await new ApiConfigImpostos({ data: payload }).update(
        payload.id_configuracao ? payload.id_configuracao.toString() : undefined
      );

      if (result.success) {
        // Volta do backend (números) -> form (strings)
        const formValues = normalizeToForm(result.data || {});
        reset(formValues);
        if (result.message) setMessages(result.message);
      } else if (result.message) {
        setMessages(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSelect = (
    fieldName:
      | "id_pis"
      | "id_inss"
      | "id_iss"
      | "id_ir"
      | "id_fgts"
      | "id_fgts_13"
      | "id_inss_13",
    label: string
  ) => (
    <Col md={6} className="mt-3" key={fieldName}>
      <Form.Group>
        <Form.Label>{label}:</Form.Label>
        <Controller
          name={fieldName as any}
          control={control}
          render={({ field }) => {
            // form guarda string|null; options têm value:string
            const selected =
              field.value == null
                ? null
                : (options.find((opt) => opt.value === field.value) ?? null);

            return (
              <SelectComp
                options={options}
                value={selected}
                onChange={(opt: any) => {
                  // SelectComp/React-Select devolve option ou null
                  field.onChange(opt ? opt.value : null);
                }}
                isClearable
                label={undefined}
              />
            );
          }}
        />
        {errors[fieldName] && (
          <div className="text-danger mt-1">{errors[fieldName]?.message}</div>
        )}
      </Form.Group>
    </Col>
  );

  return (
    <DefaultBasePage
      title="Configuração de Impostos"
      loading={loading}
      message={messages}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          {renderSelect("id_pis", "PIS")}
          {renderSelect("id_inss", "INSS")}
          {renderSelect("id_iss", "ISS")}
          {renderSelect("id_ir", "IR")}
          {renderSelect("id_fgts", "FGTS")}
          {renderSelect("id_fgts_13", "FGTS - 13º")}
          {renderSelect("id_inss_13", "INSS - 13º")}
        </Row>

        <AlterMessages messages={messages} setMessages={setMessages} />

        <div className="d-flex justify-content-end mt-4">
          <Button variant="outline-success" type="submit">
            Salvar Configuração
          </Button>
        </div>
      </Form>
    </DefaultBasePage>
  );
};

export default ConfigImpostosPrivatePage;
